import { colors, semanticColors, radius, spacing, heights, typography, shadows } from "@/styles/tokens";

// The Undo affordance for a removed quote line.
//
// ⚠️ WHY THIS IS NOT UndoBar. The two look alike on purpose — same dark
// surface, same toast shadow, same indigo Undo — but they are driven by
// different stacks and merging them would break both.
//
// UndoBar's count IS MaterialTable's `undoStack.length`, and its Undo calls
// `undoRef.current()`, which pops that component's own cell-edit stack. A
// removal does not live there: it is owned by QuoteDetail, because the rows,
// the durable store and the save payload are QuoteDetail's. Worse, a removal
// REMOUNTS MaterialTable (materialsOverride + a key bump, the same mechanism
// Add Materials uses), which discards that stack outright. So a merged bar
// would show a count drawn from a stack that had just been thrown away, and
// pressing Undo would have to decide which of two independently-ordered
// stacks the user meant. Two bars, each honest about what it undoes.
//
// It sits above UndoBar in their shared in-flow stack rather than replacing
// it: a user who has edited some cells and then removed a line has two
// separate things they might want back. Neither notification overlays the
// table or its scrollbars.
export function RemovedLineToast({
  lineNo,
  partNo,
  onUndo,
  onDismiss,
}: {
  // The number the line HAD when it was removed. Everything after it has
  // since been renumbered, so this names a position that no longer exists —
  // which is exactly why the part number is shown alongside it.
  lineNo: number;
  partNo: string;
  onUndo: () => void;
  onDismiss: () => void;
}) {
  return (
    <div
      data-testid="removed-line-toast"
      style={{
        position: "static",
        width: "100%",
        minWidth: 0,
        minHeight: heights.control,
        boxSizing: "border-box",
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: spacing[8],
        padding: `${spacing[8]}px ${spacing[16]}px`,
        backgroundColor: semanticColors.darkSurfaceBg,
        color: semanticColors.darkSurfaceText,
        borderRadius: radius.base,
        fontSize: typography.fontSize.sm,
        boxShadow: shadows.toast,
      }}
    >
      <span
        data-testid="removed-line-toast-label"
        style={{ flex: "1 1 160px", minWidth: 0, overflowWrap: "anywhere" }}
      >
        Line <span className="tabular-nums">{lineNo}</span>
        {partNo ? ` (${partNo})` : ""} removed
      </span>
      <div className="flex items-center" style={{ gap: spacing[16], flexShrink: 0, marginLeft: "auto" }}>
        <button
          data-testid="removed-line-toast-undo"
          onClick={onUndo}
          style={{
            background: "transparent",
            border: "none",
            color: colors.indigo400,
            fontSize: typography.fontSize.sm,
            fontWeight: typography.fontWeight.medium,
            cursor: "pointer",
            padding: 0,
          }}
        >
          Undo
        </button>
        {/* Dismiss, so the bar is not stuck on screen until the next removal.
            It abandons the Undo rather than performing it — the line stays
            removed, which is what the user asked for in the first place. */}
        <button
          data-testid="removed-line-toast-dismiss"
          onClick={onDismiss}
          aria-label="Dismiss"
          style={{
            background: "transparent",
            border: "none",
            color: semanticColors.darkSurfaceText,
            fontSize: typography.fontSize.sm,
            cursor: "pointer",
            padding: 0,
            opacity: 0.7,
          }}
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}
